// components/ChoosePassword.tsx
import { ChangePasswordRequest } from 'api/axios-client';
import { usePassword2Mutation } from 'api/axios-client/Query';
import { genSaltSync, hashSync } from 'bcrypt-ts';
import { Button } from 'components/button';
import { Input } from 'components/fields/input';
import React from 'react';
import { useForm, Controller, SubmitHandler } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

interface ChoosePasswordFormData {
    password: string;
    confirmPassword: string;
}

const ChoosePassword: React.FC = () => {
    const { t } = useTranslation();
    const {
        control,
        handleSubmit,
        watch,
        formState: { errors },
    } = useForm<ChoosePasswordFormData>({
        mode: 'onBlur', // Validate on blur
    });

    const passwordValue = watch('password', '');
    const { mutate, isPending } = usePassword2Mutation({
        onSuccess: () => {
            window.location.href = '/';
        },
    });
    const onSubmit: SubmitHandler<ChoosePasswordFormData> = (data) => {
        const salt = genSaltSync(10);
        const hash = hashSync(data.password, salt);
        mutate(
            new ChangePasswordRequest({
                password_hash: hash,
            })
        );
    };

    return (
        <form
            className="flex flex-col gap-3"
            onSubmit={handleSubmit(onSubmit)}
            noValidate
        >
            {/* Password Field */}
            <Controller
                name="password"
                control={control}
                rules={{
                    required: t('Password is required.'),
                    minLength: {
                        value: 8,
                        message: t(
                            'Password must be at least 8 characters long.'
                        ),
                    },
                    pattern: {
                        value: /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[@$!%*?&])[A-Za-z\d@$!%*?&]{8,}$/,
                        message: t(
                            'Password must include uppercase, lowercase, number, and special character.'
                        ),
                    },
                }}
                render={({ field }) => (
                    <Input
                        fullWidth
                        label="Password"
                        type="password"
                        placeholder={t('Enter your password')}
                        {...field}
                        error={errors.password?.message}
                    />
                )}
            />

            {/* Confirm Password Field */}
            <Controller
                name="confirmPassword"
                control={control}
                rules={{
                    required: t('Please confirm your password.'),
                    validate: (value) =>
                        value === passwordValue || t('Passwords do not match.'),
                }}
                render={({ field }) => (
                    <Input
                        fullWidth
                        label={t('Confirm Password')}
                        type="password"
                        placeholder="Re-enter your password"
                        {...field}
                        error={errors.confirmPassword?.message}
                    />
                )}
            />

            {/* Submit Button */}
            <Button isLoading={isPending} className="w-full mt-4" type="submit">
                {t('Sign Up')}
            </Button>
        </form>
    );
};

export default ChoosePassword;
